import { createClient } from "@supabase/supabase-js";
import { emergencyEvictCaches } from "./cacheVersion";

// =============================================================================
// supabase
// =============================================================================
// PURPOSE: Supabase client initialization with cold start handling.
//
// REQUIREMENTS:
// - Reads URL and anon key from environment variables ONLY (no localStorage fallback)
// - Auto-refresh tokens, persist session, detect session in URL (for email links)
// - Warm-up ping on app load to wake PostgREST connection pool (dbReady promise)
//
// SECURITY:
// - NEVER store credentials in localStorage (XSS attack vector)
// - Anon key is safe for public use (RLS policies enforce data access)
// - Service role key NEVER exposed to frontend (N8N workflows only)
//
// KEY DECISIONS:
// - dbReady promise exported for AuthContext to await before first query (prevents cold start delays)
// - isSupabaseConfigured flag prevents crashes when env vars missing
// - Warm-up query silent fails (best-effort, auth retries on its own)
// =============================================================================

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    "Supabase credentials not configured. " +
      "Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables in .env.local"
  );
}

// Resilient storage adapter — catches QuotaExceededError, evicts altara_* caches,
// and retries. If localStorage is permanently broken (Brave ephemeral partition),
// falls back to sessionStorage so auth still works for the session.
const resilientStorage =
  typeof window !== "undefined"
    ? {
        getItem: (key: string): string | null => {
          try {
            return localStorage.getItem(key);
          } catch {
            return sessionStorage.getItem(key);
          }
        },
        setItem: (key: string, value: string): void => {
          try {
            localStorage.setItem(key, value);
          } catch {
            // QuotaExceededError — evict all altara_* data caches and retry
            emergencyEvictCaches();
            try {
              localStorage.setItem(key, value);
            } catch {
              // localStorage permanently broken — fall back to sessionStorage
              console.warn(
                `[Auth] localStorage write failed after eviction, falling back to sessionStorage for ${key}`
              );
              sessionStorage.setItem(key, value);
            }
          }
        },
        removeItem: (key: string): void => {
          try {
            localStorage.removeItem(key);
          } catch {
            // ignore
          }
          try {
            sessionStorage.removeItem(key);
          } catch {
            // ignore
          }
        },
      }
    : undefined;

export const supabase =
  supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
          // Resilient adapter: localStorage → evict caches on quota error → sessionStorage fallback
          storage: resilientStorage,
        },
      })
    : null;

export const isSupabaseConfigured = !!supabase;

// Warm-up ping: wake the PostgREST connection pool on app load.
// Export the promise so AuthContext can await it before querying.
export const dbReady: Promise<void> = supabase
  ? (supabase.from("profiles").select("id").limit(1) as unknown as Promise<unknown>)
      .then(() => {
        // Warm-up complete - connection pool is ready
      })
      .catch(() => {
        // Silent — best-effort. Auth will retry on its own.
      })
  : Promise.resolve();
